/**
 * Config Registry API Service
 * API methods for Config Registry feature
 */

import { CMSApiResponse } from "framework/http/CMSApiClient";
import { RunContextApp } from "framework/RCApp";
import {
  ConfigRegistryItem,
  ConfigTypesResponse,
  UISchemaResponse,
  PaginationMeta,
  ConfigRegistryAPI as APITypes,
  Template,
  TemplateListResponse,
  TemplateDiffResult,
  TemplateUpdatePayload,
} from "../types/configRegistryTypes";

// Extended response type that includes meta from the API
// NOTE: CMSApiClient returns response.data which is the full API response { data, meta }
// So when we access response.data, we get the configs array, not { data, meta }
// The meta is at the same level in the raw response
//
// IMPORTANT: Config registry API returns FLAT meta structure (not nested under pagination)
// Backend returns: { data: [...], meta: { page, pageSize, pageCount, total, hasMore } }
export interface ConfigListApiResponse extends CMSApiResponse<ConfigRegistryItem[]> {
  meta?: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
    hasMore?: boolean;
  };
}

// =============================================================================
// API ENDPOINTS
// =============================================================================

const ENDPOINTS = {
  CONFIGS_LIST: '/config-registry/v1/configs',
  CONFIG_DETAIL: (id: string) => `/config-registry/v1/configs/${id}`,
  CONFIGS_CRUD: '/config-registry-configs',
  CONFIG_UPDATE: (id: string) => `/config-registry-configs/${id}`,
  CONFIG_TYPES: '/config-registry-configs/config-types',
  UI_SCHEMA: '/config-registry-lookup-types/v1/ui-schema',
  CREATE_LOOKUP: '/config-registry-lookups/v1/create',
  WORKFLOW_SUBMIT: (id: string) => `/config-registry-config/v1/${id}/submit`,
  WORKFLOW_APPROVE: (id: string) => `/config-registry-config/v1/${id}/approve`,
  WORKFLOW_REJECT: (id: string) => `/config-registry-config/v1/${id}/reject`,
  WORKFLOW_REOPEN: (id: string) => `/config-registry-config/v1/${id}/reopen`,
  WORKFLOW_PUBLISH: (id: string) => `/config-registry-config/v1/${id}/publish`,
  WORKFLOW_UNPUBLISH: (id: string) => `/config-registry-config/v1/${id}/unpublish`,
  VALIDATE: '/config-registry-configs/v1/validate',
  // Template endpoints
  TEMPLATES_LIST: '/config-registry-templates',
  TEMPLATE_DETAIL: (id: string) => `/config-registry-templates/${id}`,
  TEMPLATE_CLONE: (id: string) => `/config-registry-template/v1/${id}/clone`,
  TEMPLATE_DIFF: '/config-registry-template/v1/diff',
};

// =============================================================================
// API SERVICE
// =============================================================================

export const ConfigRegistryApis = {
  /**
   * Get list of configs with pagination and filters
   * Note: API returns { data: [...], meta: { pagination: {...} } }
   * CMSApiClient interprets this as CMSApiResponse where data = the array
   */
  getConfigs: async (
    rc: RunContextApp,
    params: APITypes.ListParams = {}
  ): Promise<ConfigListApiResponse> => {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    // Basic filters
    if (params.configType) queryParams.append('configType', params.configType);
    // Use verificationStatus if provided, otherwise fall back to approvalStatus for backward compatibility
    if (params.verificationStatus) queryParams.append('verificationStatus', params.verificationStatus);
    else if (params.approvalStatus) queryParams.append('verificationStatus', params.approvalStatus);
    if (params.expand) queryParams.append('expand', params.expand);
    if (params.search) queryParams.append('search', params.search);

    // Lookup type filters (documentIds)
    if (params.component) queryParams.append('component', params.component);
    if (params.section) queryParams.append('section', params.section);
    if (params.classification) queryParams.append('classification', params.classification);
    if (params.fileType) queryParams.append('fileType', params.fileType);
    if (params.restricted && params.restricted !== 'all') queryParams.append('restricted', params.restricted);

    const queryString = queryParams.toString();
    const url = `${ENDPOINTS.CONFIGS_LIST}${queryString ? `?${queryString}` : ''}`;

    return rc.cmsApiClient.get<ConfigRegistryItem[]>(url) as Promise<ConfigListApiResponse>;
  },

  /**
   * Get single config by documentId
   */
  getConfig: async (
    rc: RunContextApp,
    documentId: string,
    expand: boolean = true
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    const populateRelations = 'populate[parentConfig][fields][0]=configType&populate[parentConfig][fields][1]=configKey&populate[parentConfig][fields][2]=displayName&populate[relatedConfigs][fields][0]=configType&populate[relatedConfigs][fields][1]=configKey&populate[relatedConfigs][fields][2]=displayName&populate[duplicateConfig][fields][0]=configType&populate[duplicateConfig][fields][1]=configKey&populate[duplicateConfig][fields][2]=displayName&populate[configDependsUpon][fields][0]=configType&populate[configDependsUpon][fields][1]=configKey&populate[configDependsUpon][fields][2]=displayName';
    const url = expand
      ? `${ENDPOINTS.CONFIG_DETAIL(documentId)}?expand=full&${populateRelations}`
      : `${ENDPOINTS.CONFIG_DETAIL(documentId)}?${populateRelations}`;
    return rc.cmsApiClient.get<{ data: ConfigRegistryItem }>(url);
  },

  /**
   * Get list of config types for dropdown
   */
  getConfigTypes: async (
    rc: RunContextApp
  ): Promise<CMSApiResponse<ConfigTypesResponse>> => {
    return rc.cmsApiClient.get<ConfigTypesResponse>(ENDPOINTS.CONFIG_TYPES);
  },

  /**
   * Get UI schema for lookup dropdowns
   */
  getUISchema: async (
    rc: RunContextApp
  ): Promise<CMSApiResponse<UISchemaResponse>> => {
    return rc.cmsApiClient.get<UISchemaResponse>(ENDPOINTS.UI_SCHEMA);
  },

  /**
   * Create a new config
   */
  createConfig: async (
    rc: RunContextApp,
    data: APITypes.CreatePayload
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.CONFIGS_CRUD,
      { data }
    );
  },

  /**
   * Update an existing config
   * Uses PUT method as required by Strapi v5 for updates
   */
  updateConfig: async (
    rc: RunContextApp,
    documentId: string,
    data: Partial<APITypes.CreatePayload>
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.put<{ data: ConfigRegistryItem }>(
      ENDPOINTS.CONFIG_UPDATE(documentId),
      { data }
    );
  },

  /**
   * Submit config for approval (DRAFT -> READY_FOR_QA)
   */
  submitForApproval: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_SUBMIT(documentId),
      {}
    );
  },

  /**
   * Approve config (READY_FOR_QA -> APPROVED)
   */
  approveConfig: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_APPROVE(documentId),
      {}
    );
  },

  /**
   * Reject config (READY_FOR_QA -> REJECTED)
   */
  rejectConfig: async (
    rc: RunContextApp,
    documentId: string,
    reason?: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_REJECT(documentId),
      { reason }
    );
  },

  /**
   * Reopen config for editing (APPROVED -> TODO)
   */
  reopenConfig: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_REOPEN(documentId),
      {}
    );
  },

  /**
   * Publish approved config (draft -> published)
   */
  publishConfig: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_PUBLISH(documentId),
      {}
    );
  },

  /**
   * Unpublish config (published -> draft)
   */
  unpublishConfig: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_UNPUBLISH(documentId),
      {}
    );
  },

  /**
   * Validate config data before saving
   */
  validateConfig: async (
    rc: RunContextApp,
    data: Partial<APITypes.CreatePayload>
  ): Promise<CMSApiResponse<{ valid: boolean; errors?: { field: string; message: string }[] }>> => {
    return rc.cmsApiClient.post<{ valid: boolean; errors?: { field: string; message: string }[] }>(
      ENDPOINTS.VALIDATE,
      data
    );
  },

  /**
   * Create a new lookup value
   */
  createLookup: async (
    rc: RunContextApp,
    data: {
      code: string;
      displayName: string;
      lookupType: string;
      parentLookup?: string;
      description?: string;
    }
  ): Promise<CMSApiResponse<{
    data: {
      documentId: string;
      code: string;
      displayName: string;
      description?: string;
      lookupType: { documentId: string; code: string; displayName: string };
      parentLookup?: { documentId: string; code: string; displayName: string } | null;
    };
    message: string;
  }>> => {
    return rc.cmsApiClient.post(ENDPOINTS.CREATE_LOOKUP, data);
  },

  // ===========================================================================
  // TEMPLATE APIs
  // ===========================================================================

  /**
   * Get list of templates
   */
  getTemplates: async (
    rc: RunContextApp
  ): Promise<CMSApiResponse<TemplateListResponse>> => {
    const url = `${ENDPOINTS.TEMPLATES_LIST}?populate=items.configRegistryConfig`;
    return rc.cmsApiClient.get<TemplateListResponse>(url);
  },

  /**
   * Get single template by documentId
   */
  getTemplate: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: Template }>> => {
    const url = `${ENDPOINTS.TEMPLATE_DETAIL(documentId)}?populate=items.configRegistryConfig`;
    return rc.cmsApiClient.get<{ data: Template }>(url);
  },

  /**
   * Update a template (add/remove/update items)
   */
  updateTemplate: async (
    rc: RunContextApp,
    documentId: string,
    data: TemplateUpdatePayload
  ): Promise<CMSApiResponse<{ data: Template }>> => {
    return rc.cmsApiClient.put<{ data: Template }>(
      ENDPOINTS.TEMPLATE_DETAIL(documentId),
      { data }
    );
  },

  /**
   * Clone a template
   */
  cloneTemplate: async (
    rc: RunContextApp,
    documentId: string,
    name?: string,
    description?: string
  ): Promise<CMSApiResponse<{ data: Template; message: string }>> => {
    return rc.cmsApiClient.post<{ data: Template; message: string }>(
      ENDPOINTS.TEMPLATE_CLONE(documentId),
      { name, description }
    );
  },

  /**
   * Compare two templates
   */
  diffTemplates: async (
    rc: RunContextApp,
    sourceId: string,
    targetId: string
  ): Promise<CMSApiResponse<TemplateDiffResult>> => {
    const url = `${ENDPOINTS.TEMPLATE_DIFF}?sourceId=${sourceId}&targetId=${targetId}`;
    return rc.cmsApiClient.get<TemplateDiffResult>(url);
  },
};

